import { Component, computed, inject, input, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { SupportTopicsFaqBottomSheetComponent } from '../support-topics-faq-bottom-sheet/support-topics-faq-bottom-sheet.component';

export interface SupportTopicFaq {
  questionKey: string;
  answerKey: string;
}

export interface SupportTopic {
  id: string;
  labelKey: string;
  faqs: SupportTopicFaq[];
}

@Component({
  selector: 'app-support-topics',
  standalone: false,
  templateUrl: './support-topics.component.html',
  styleUrl: './support-topics.component.scss',
})
export class SupportTopicsComponent {
  private readonly bottomSheet = inject(MatBottomSheet);

  /** Translation key for the section title (used when headerText is null) */
  title = input<string>('contact.topics.title');

  /** Translation key for the section description (used when subtextText is null) */
  description = input<string>('contact.topics.description');

  /** Translation key for the sidebar heading */
  sidebarTitle = input<string>('contact.topics.sidebar_title');

  /**
   * Raw title text from the backend (contact page settings).
   * null  → fall back to the `title` translation key (default behaviour).
   * ''    → hide the title entirely.
   * other → display as-is, no translation pipe.
   */
  headerText = input<string | null>(null);

  /**
   * Raw description text from the backend (contact page settings).
   * null  → fall back to the `description` translation key (default behaviour).
   * ''    → hide the description entirely.
   * other → display as-is, no translation pipe.
   */
  subtextText = input<string | null>(null);

  /** Topics to filter by, each with its own FAQ list */
  topics = input<SupportTopic[]>([]);

  private selectedTopicId = signal<string | null>(null);
  // -1 = no FAQ expanded — all accordion items start collapsed
  expandedFaqIndex = signal<number>(-1);

  // Show the header block only when at least title or description will render something
  readonly showHeader = computed(() => {
    const h = this.headerText();
    const s = this.subtextText();
    if (h === null && s === null) return true; // both using translation keys — always show
    return !!(h || s);
  });

  activeTopic = computed<SupportTopic | null>(() => {
    const topics = this.topics();
    if (!topics.length) return null;
    const id = this.selectedTopicId();
    return (id && topics.find((topic) => topic.id === id)) || topics[0];
  });

  isActiveTopic(id: string): boolean {
    return this.activeTopic()?.id === id;
  }

  selectTopic(id: string): void {
    this.selectedTopicId.set(id);
    this.expandedFaqIndex.set(-1);
  }

  toggleFaq(index: number): void {
    this.expandedFaqIndex.update((current) => (current === index ? -1 : index));
  }

  /**
   * Mobile-only (<=768px): tapping a row in the Support Topics sidebar opens
   * its FAQ accordion as a bottom sheet instead of the desktop's inline list.
   */
  openMobileFaq(topic: SupportTopic): void {
    this.bottomSheet.open(SupportTopicsFaqBottomSheetComponent, {
      panelClass: 'support-topics-faq-bottom-sheet-container',
      data: { topic },
    });
  }
}
